import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type {
  BrowserEngine,
  BrowserViewportPreset,
  ExploratoryEffort,
  ReviewFlavor,
} from '@chatsdet/types';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type {
  ExploratoryTestsResponse,
  ExploratoryTestDetailResponse,
  ExploratoryTestRunsResponse,
  ExploratoryTestSchedulesResponse,
  StartExploratoryTestResponse,
} from './types';

type PaginationOptions = {
  page?: number;
  pageSize?: number;
  /** Restrict to a review flavor — Labs "UX Review" passes 'ux', Exploratory 'functional'. */
  flavor?: ReviewFlavor;
};

export function useExploratoryTests(options?: PaginationOptions) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;
  const flavor = options?.flavor;

  return useQuery({
    queryKey: ['exploratory-tests', page, pageSize, flavor ?? 'all'],
    staleTime: 5_000,
    refetchInterval: (query) => {
      const data = query.state.data as ExploratoryTestsResponse | undefined;
      if (!data) return false;
      // Tier the cadence to mirror the detail view: nothing user-visible
      // changes on the list during planning for minutes at a time, so 10s
      // is plenty when only `planning` rows are active. Bump to 3s only
      // when there's actual tester / queue activity to surface.
      const hasFastChange = data.items.some(
        (t) => t.status === 'running' || t.status === 'pending'
      );
      if (hasFastChange) return 3_000;
      const hasPlanning = data.items.some((t) => t.status === 'planning');
      if (hasPlanning) return 10_000;
      return false;
    },
    queryFn: async (): Promise<ExploratoryTestsResponse> => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (flavor) params.append('flavor', flavor);

      const response = await fetch(`${API_URL}/adhoc-tests?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch exploratory tests');
      }

      return response.json();
    },
  });
}

export function useExploratoryTest(id: string | undefined) {
  return useQuery({
    queryKey: ['exploratory-test', id],
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data as ExploratoryTestDetailResponse | undefined;
      const row = data?.data;
      const status = row?.status;
      // Tier the cadence: nothing user-visible changes during planning for
      // minutes at a time, so 10s is plenty there. Tester phase needs the
      // 3s cadence so live screenshot/timeline updates feel snappy.
      if (status === 'planning') {
        // Stop polling if a planning row has been stuck past the 15-min
        // budget + 1-min slack — nothing the API can tell us is going to
        // change at this point and continued polling is wasted load. The
        // user can manually refresh; the stuck-row sweep should reconcile.
        if (row?.planningStartedAt) {
          const elapsed = Date.now() - Date.parse(row.planningStartedAt);
          if (Number.isFinite(elapsed) && elapsed > 16 * 60 * 1000) {
            return false;
          }
        }
        return 10_000;
      }
      if (status === 'running' || status === 'pending') return 3_000;
      return false;
    },
    queryFn: async () => {
      const response = await fetch(`${API_URL}/adhoc-tests/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch exploratory test');
      }

      return response.json() as Promise<ExploratoryTestDetailResponse>;
    },
  });
}

/**
 * List every run of a durable exploratory-test parent (the run-history strip).
 * `parentId` is the `exploratoryTestId` carried on each run row; the query is
 * disabled for parent-less runs (PR/ticket/legacy). Polls every 3s while any
 * run is in flight so the strip updates live, then goes quiet.
 */
export function useExploratoryTestRuns(parentId: string | undefined) {
  return useQuery({
    queryKey: ['exploratory-test-runs', parentId],
    enabled: !!parentId,
    refetchInterval: (query) => {
      const data = query.state.data as ExploratoryTestRunsResponse | undefined;
      if (!data) return false;
      const inFlight = data.runs.some(
        (r) => r.status === 'running' || r.status === 'pending' || r.status === 'planning'
      );
      return inFlight ? 3_000 : false;
    },
    queryFn: async (): Promise<ExploratoryTestRunsResponse> => {
      const response = await fetch(`${API_URL}/exploratory-tests/${parentId}/runs`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch run history');
      }

      return response.json();
    },
  });
}

/**
 * Re-run a durable exploratory test from its parent's stored prompt, target,
 * and attached context. Takes the parent id (`exploratoryTestId`); on success
 * navigates to the freshly-created run so the user watches it live.
 */
export function useRerunExploratoryTest() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (parentId: string): Promise<StartExploratoryTestResponse> => {
      const response = await fetch(`${API_URL}/exploratory-tests/${parentId}/runs`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to re-run exploratory test');
      }

      return response.json();
    },
    onSuccess: (data, parentId) => {
      queryClient.invalidateQueries({ queryKey: ['exploratory-tests'] });
      queryClient.invalidateQueries({ queryKey: ['exploratory-test-runs', parentId] });
      navigate({
        to: '/exploratory/$exploratoryTestId',
        params: { exploratoryTestId: data.adhocTestId },
      });
    },
  });
}

const SCHEDULES_KEY = ['exploratory-schedules'] as const;

/**
 * List exploratory-test schedules. With `parentId`, only that parent's
 * schedules (the run-detail modal); without it, all org schedules (the
 * list-page "Scheduled" section).
 */
export function useExploratorySchedules(parentId?: string) {
  return useQuery({
    queryKey: [...SCHEDULES_KEY, parentId ?? 'all'],
    queryFn: async (): Promise<ExploratoryTestSchedulesResponse> => {
      const url = parentId
        ? `${API_URL}/exploratory-tests/schedules?parentId=${encodeURIComponent(parentId)}`
        : `${API_URL}/exploratory-tests/schedules`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch schedules');
      }
      return response.json();
    },
  });
}

// Generic schedule-rule lifecycle hooks (parse / toggle / run-now / delete) live
// in the shared scheduling module and back every scheduling surface; re-exported
// here so existing exploratory import sites keep working.
export {
  useParseSchedule,
  useToggleScheduleRule,
  useRunScheduleNow,
  useDeleteScheduleRule,
} from '../scheduling';

/**
 * Create a recurring schedule for an exploratory test by minting a
 * schedule-triggered `exploratory-test.run` automation rule.
 */
export function useCreateExploratorySchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      parentId: string;
      cron: string;
      timezone: string;
      description: string;
    }) => {
      const response = await fetch(`${API_URL}/org/automation/rules`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: input.description,
          triggerSource: 'schedule',
          triggerEvent: 'scheduled',
          triggerConfig: {
            scheduleType: 'cron',
            scheduleConfig: { pattern: input.cron, timezone: input.timezone },
          },
          actionType: 'exploratory-test.run',
          actionConfig: { exploratory_test_id: input.parentId },
        }),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to create schedule');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
  });
}

// The context library is now a first-class Test Studio feature; its canonical
// client type and hooks live in features/context-library. Re-exported here so
// existing exploratory import sites keep working.
export {
  useContextDocuments,
  useUploadContextDocument,
  useAddContextLink,
  useAddContextPaste,
  useUpdateContextDocument,
  useDeleteContextDocument,
  type ContextDocument,
} from '../context-library/useContextLibrary';

export function useStartExploratoryTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: {
      propertyId: string;
      credentialId?: string | null;
      instructions: string;
      startUrl?: string | null;
      target?:
        | {
            kind: 'live';
            credentialId?: string | null;
            environmentId?: string | null;
            /** UX flavor: mint a throwaway sign-up identity; run starts logged out. */
            provisionTempIdentity?: boolean;
          }
        | { kind: 'sandbox'; environmentTemplateId: string }
        | { kind: 'sandbox-v2-environment'; environment: string };
      /** Review flavor: 'functional' (default) or 'ux' walkthrough. */
      flavor?: ReviewFlavor;
      /** Time budget; only for flavors whose capabilities declare an effort (the fuzzer). */
      effort?: ExploratoryEffort;
      /** Browser engine for this exploratory test. */
      browserEngine?: BrowserEngine;
      /** Viewport preset for this exploratory test. */
      viewportSize?: BrowserViewportPreset;
      /** Context-library document ids to attach to the minted parent. */
      contextDocumentIds?: string[];
    }): Promise<StartExploratoryTestResponse> => {
      const response = await fetch(`${API_URL}/adhoc-tests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(options),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to start exploratory test');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exploratory-tests'] });
    },
  });
}

/** Confirm / dismiss-false-positive feedback on a UX review finding. */
export function useSubmitFindingFeedback(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      findingId: string;
      state: 'confirmed' | 'dismissed_false_positive';
      note?: string | null;
    }) => {
      const response = await fetch(
        `${API_URL}/adhoc-tests/${runId}/findings/${input.findingId}/feedback`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ state: input.state, note: input.note ?? null }),
        }
      );
      if (!response.ok) throw await toApiError(response, 'Failed to record feedback');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exploratory-test', runId] });
    },
  });
}

/** Promote a UX review finding to an Issue (manual, idempotent). */
export function usePromoteFinding(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      findingId: string
    ): Promise<{ ok: boolean; issueId: string; promoted: boolean }> => {
      const response = await fetch(
        `${API_URL}/adhoc-tests/${runId}/findings/${findingId}/promote`,
        { method: 'POST', credentials: 'include' }
      );
      if (!response.ok) throw await toApiError(response, 'Failed to promote finding');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exploratory-test', runId] });
    },
  });
}

export function useCancelExploratoryTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_URL}/adhoc-tests/${id}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to cancel exploratory test');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exploratory-tests'] });
    },
  });
}

/** Start a separate PR test run from the current GitHub head. */
export function useRetestPrTest() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (input: { sourceRunId: string; instructions?: string }) => {
      const response = await fetch(`${API_URL}/adhoc-tests/${input.sourceRunId}/retest`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ instructions: input.instructions }),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to retest pull request');
      return response.json() as Promise<StartExploratoryTestResponse>;
    },
    onSuccess: (data, input) => {
      queryClient.invalidateQueries({ queryKey: ['exploratory-tests'] });
      queryClient.invalidateQueries({ queryKey: ['exploratory-test', input.sourceRunId] });
      navigate({
        to: '/exploratory/$exploratoryTestId',
        params: { exploratoryTestId: data.adhocTestId },
      });
    },
  });
}
